import React, { useEffect, useMemo, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import "./Hero.css";
import Button from "../../components/button/Button";
import { useTranslation } from "react-i18next";

// Mobile videos (900p, CRF 28)
import clip1WebmMobile from "../../assets/vid_1_mobile.webm";
import clip1Mp4Mobile from "../../assets/vid_1_mobile.mp4";

// Desktop videos (1080p, CRF 26)
import clip1WebmDesktop from "../../assets/vid_1_desktop.webm";
import clip1Mp4Desktop from "../../assets/vid_1_desktop.mp4";

const CLIP_DURATION_MS = 27000;

const usePrefersReducedMotion = () =>
  typeof window !== "undefined" &&
  window.matchMedia &&
  window.matchMedia("(prefers-reduced-motion: reduce)").matches;

/* ---- tiny reveal-on-scroll hook ---- */
function useReveal(options = { threshold: 0.18, rootMargin: "0px 0px -10% 0px" }) {
  const ref = useRef(null);
  const [inView, setInView] = useState(false);

  useEffect(() => {
    if (!ref.current || inView) return;
    if (!("IntersectionObserver" in window)) {
      setInView(true);
      return;
    }
    const io = new IntersectionObserver(([entry]) => {
      if (entry.isIntersecting) {
        setInView(true);
        io.disconnect();
      }
    }, options);
    io.observe(ref.current);
    return () => io.disconnect();
  }, [inView, options]);

  return [ref, inView];
}

const Hero = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const reduceMotion = usePrefersReducedMotion();

  // Detect if viewport is mobile (< 768px)
  const [isMobile, setIsMobile] = useState(window.innerWidth < 768);

  useEffect(() => {
    const handleResize = () => setIsMobile(window.innerWidth < 768);
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  // Select video sources based on viewport
  const sources = useMemo(
    () => {
      const webmSrc = isMobile ? clip1WebmMobile : clip1WebmDesktop;
      const mp4Src = isMobile ? clip1Mp4Mobile : clip1Mp4Desktop;
      return [
        [
          { src: webmSrc, type: "video/webm" },
          { src: mp4Src, type: "video/mp4" }
        ],
        [
          { src: webmSrc, type: "video/webm" },
          { src: mp4Src, type: "video/mp4" }
        ],
      ];
    },
    [isMobile]
  );

  // double-video crossfade
  const [active, setActive] = useState(0);
  const refs = [useRef(null), useRef(null)];

  const loadClip = (videoEl, i) => {
    if (!videoEl) return;
    videoEl.innerHTML = "";
    sources[i % sources.length].forEach(({ src, type }) => {
      const s = document.createElement("source");
      s.src = src;
      s.type = type;
      videoEl.appendChild(s);
    });
    videoEl.load();
    videoEl.currentTime = 0;
    videoEl.play().catch(() => {});
  };

  useEffect(() => {
    if (reduceMotion || sources.length === 0) return;

    loadClip(refs[0].current, 0);
    loadClip(refs[1].current, 1);

    let current = 1;
    let nextIndex = 0;

    const interval = setInterval(() => {
      setActive((a) => 1 - a);
      const hiddenVideo = refs[current].current;
      nextIndex = (nextIndex + 1) % sources.length;
      loadClip(hiddenVideo, nextIndex);
      current = 1 - current;
    }, CLIP_DURATION_MS);

    return () => clearInterval(interval);
  }, [reduceMotion, sources, sources.length]);

  // reveal hooks for content & CTA
  const [contentRef, contentIn] = useReveal();
  const [ctaRef, ctaIn] = useReveal();

  return (
    <section id="hero" className="hero">
      {!reduceMotion && (
        <div className="hero__bg">
          <video
            ref={refs[0]}
            className={`hero__video ${active === 0 ? "is-active" : ""}`}
            playsInline
            muted
            autoPlay
            loop
            preload="auto"
          />
          <video
            ref={refs[1]}
            className={`hero__video ${active === 1 ? "is-active" : ""}`}
            playsInline
            muted
            autoPlay
            loop
            preload="auto"
          />
          <div className="hero__overlay" />
        </div>
      )}

      {reduceMotion && <div className="hero__poster" aria-hidden="true" />}

      <div className="hero__container">
        <div className="column left">
          <div
            ref={contentRef}
            className={`hero__content reveal reveal--up ${contentIn ? "in" : ""}`}
          >
            <h1 className="heading reveal-item" style={{ transitionDelay: "80ms", color: "#fff" }}>
              {t("hero.heading")}
            </h1>
            <p className="description reveal-item" style={{ transitionDelay: "180ms" }}>
              {t("hero.description")}
            </p>
          </div>

          <div
            ref={ctaRef}
            className={`flex__center buttons__wrapper button_hero reveal reveal--up ${ctaIn ? "in" : ""}`}
          >
            <Button
              className="explore_bth"
              color="linear-gradient(135deg, rgba(31,35,62,1) 0%, #3244ba 100%)"
              outline="#ffffff"
              onClick={() => navigate("/contact")}
            >
              {t("cta.getQuote")}
            </Button>
          </div>
        </div>

        <div className="column right-spacer" aria-hidden="true" />
      </div>
    </section>
  );
};

export default Hero;
